import { useState, useMemo, useEffect, useCallback } from "react";
import { ChevronDown, ChevronRight, Save, X, Check, Search, Loader } from "lucide-react";
import { countries } from "../../constants/countries";
import { VERTICAL_OPTIONS } from "../../constants/verticals";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { adminLeadsService, type StoreCensusApp, type StoreCensusAppCategory } from "../../services/admin/leads.admin.service";

// State/province data for major countries
const statesByCountry: Record<string, { code: string; name: string }[]> = {
  US: [
    { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
    { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
    { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
    { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
    { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
    { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
    { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
    { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
    { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "District of Columbia" },
  ],
  CA: [
    { code: "AB", name: "Alberta" }, { code: "BC", name: "British Columbia" }, { code: "MB", name: "Manitoba" },
    { code: "NB", name: "New Brunswick" }, { code: "NL", name: "Newfoundland and Labrador" },
    { code: "NS", name: "Nova Scotia" }, { code: "NT", name: "Northwest Territories" },
    { code: "NU", name: "Nunavut" }, { code: "ON", name: "Ontario" }, { code: "PE", name: "Prince Edward Island" },
    { code: "QC", name: "Quebec" }, { code: "SK", name: "Saskatchewan" }, { code: "YT", name: "Yukon" },
  ],
  AU: [
    { code: "NSW", name: "New South Wales" }, { code: "VIC", name: "Victoria" },
    { code: "QLD", name: "Queensland" }, { code: "WA", name: "Western Australia" },
    { code: "SA", name: "South Australia" }, { code: "TAS", name: "Tasmania" },
    { code: "ACT", name: "Australian Capital Territory" }, { code: "NT", name: "Northern Territory" },
  ],
  GB: [
    { code: "ENG", name: "England" }, { code: "SCT", name: "Scotland" },
    { code: "WLS", name: "Wales" }, { code: "NIR", name: "Northern Ireland" },
  ],
  IN: [
    { code: "AP", name: "Andhra Pradesh" }, { code: "AR", name: "Arunachal Pradesh" },
    { code: "AS", name: "Assam" }, { code: "BR", name: "Bihar" }, { code: "CT", name: "Chhattisgarh" },
    { code: "GA", name: "Goa" }, { code: "GJ", name: "Gujarat" }, { code: "HR", name: "Haryana" },
    { code: "HP", name: "Himachal Pradesh" }, { code: "JK", name: "Jammu and Kashmir" },
    { code: "JH", name: "Jharkhand" }, { code: "KA", name: "Karnataka" }, { code: "KL", name: "Kerala" },
    { code: "MP", name: "Madhya Pradesh" }, { code: "MH", name: "Maharashtra" }, { code: "MN", name: "Manipur" },
    { code: "ML", name: "Meghalaya" }, { code: "MZ", name: "Mizoram" }, { code: "NL", name: "Nagaland" },
    { code: "OR", name: "Odisha" }, { code: "PB", name: "Punjab" }, { code: "RJ", name: "Rajasthan" },
    { code: "SK", name: "Sikkim" }, { code: "TN", name: "Tamil Nadu" }, { code: "TG", name: "Telangana" },
    { code: "TR", name: "Tripura" }, { code: "UP", name: "Uttar Pradesh" }, { code: "UK", name: "Uttarakhand" },
    { code: "WB", name: "West Bengal" }, { code: "DL", name: "Delhi" },
  ],
  DE: [
    { code: "BW", name: "Baden-Württemberg" }, { code: "BY", name: "Bavaria" }, { code: "BE", name: "Berlin" },
    { code: "BB", name: "Brandenburg" }, { code: "HB", name: "Bremen" }, { code: "HH", name: "Hamburg" },
    { code: "HE", name: "Hesse" }, { code: "MV", name: "Mecklenburg-Vorpommern" },
    { code: "NI", name: "Lower Saxony" }, { code: "NW", name: "North Rhine-Westphalia" },
    { code: "RP", name: "Rhineland-Palatinate" }, { code: "SL", name: "Saarland" },
    { code: "SN", name: "Saxony" }, { code: "ST", name: "Saxony-Anhalt" },
    { code: "SH", name: "Schleswig-Holstein" }, { code: "TH", name: "Thuringia" },
  ],
};

const currencyOptions = [
  { value: "USD", label: "USD – US Dollar" },
  { value: "EUR", label: "EUR – Euro" },
  { value: "GBP", label: "GBP – British Pound" },
  { value: "CAD", label: "CAD – Canadian Dollar" },
  { value: "AUD", label: "AUD – Australian Dollar" },
  { value: "JPY", label: "JPY – Japanese Yen" },
  { value: "CHF", label: "CHF – Swiss Franc" },
  { value: "CNY", label: "CNY – Chinese Yuan" },
  { value: "SEK", label: "SEK – Swedish Krona" },
  { value: "NZD", label: "NZD – New Zealand Dollar" },
  { value: "MXN", label: "MXN – Mexican Peso" },
  { value: "SGD", label: "SGD – Singapore Dollar" },
  { value: "HKD", label: "HKD – Hong Kong Dollar" },
  { value: "NOK", label: "NOK – Norwegian Krone" },
  { value: "KRW", label: "KRW – South Korean Won" },
  { value: "TRY", label: "TRY – Turkish Lira" },
  { value: "INR", label: "INR – Indian Rupee" },
  { value: "RUB", label: "RUB – Russian Ruble" },
  { value: "BRL", label: "BRL – Brazilian Real" },
  { value: "ZAR", label: "ZAR – South African Rand" },
  { value: "DKK", label: "DKK – Danish Krone" },
  { value: "PLN", label: "PLN – Polish Zloty" },
  { value: "THB", label: "THB – Thai Baht" },
  { value: "IDR", label: "IDR – Indonesian Rupiah" },
  { value: "HUF", label: "HUF – Hungarian Forint" },
  { value: "CZK", label: "CZK – Czech Koruna" },
  { value: "ILS", label: "ILS – Israeli Shekel" },
  { value: "CLP", label: "CLP – Chilean Peso" },
  { value: "PHP", label: "PHP – Philippine Peso" },
  { value: "AED", label: "AED – UAE Dirham" },
  { value: "COP", label: "COP – Colombian Peso" },
  { value: "SAR", label: "SAR – Saudi Riyal" },
  { value: "MYR", label: "MYR – Malaysian Ringgit" },
  { value: "RON", label: "RON – Romanian Leu" },
  { value: "NGN", label: "NGN – Nigerian Naira" },
  { value: "ARS", label: "ARS – Argentine Peso" },
  { value: "EGP", label: "EGP – Egyptian Pound" },
  { value: "VND", label: "VND – Vietnamese Dong" },
  { value: "PKR", label: "PKR – Pakistani Rupee" },
  { value: "BDT", label: "BDT – Bangladeshi Taka" },
  { value: "QAR", label: "QAR – Qatari Riyal" },
  { value: "KWD", label: "KWD – Kuwaiti Dinar" },
];

const contactTypeOptions = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
];

const emailTypeOptions = [
  { value: "founder_personal", label: "Founder Personal" },
  { value: "personal", label: "Personal" },
  { value: "generic", label: "Generic" },
  { value: "support", label: "Support" },
  { value: "sales", label: "Sales" },
  { value: "info", label: "Info" },
];

const employeeSizeOptions = [
  { value: "1-10", label: "1–10" },
  { value: "11-50", label: "11–50" },
  { value: "51-200", label: "51–200" },
  { value: "201-500", label: "201–500" },
  { value: "501-1000", label: "501–1,000" },
  { value: "1001-5000", label: "1,001–5,000" },
  { value: "5001-10000", label: "5,001–10,000" },
  { value: "10001+", label: "10,001+" },
];

const freshnessPresetOptions = [
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "last_90_days", label: "Last 90 days" },
];

// --- Reusable sub-components ---

interface FilterBuilderProps {
  filters: Record<string, any>;
  onFiltersChange: (filters: Record<string, any>) => void;
  onSaveFilter?: (name: string, description: string) => void;
}

interface SectionProps {
  title: string;
  count: number;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function FilterSection({ title, count, isOpen, onToggle, children }: SectionProps) {
  return (
    <div className="border border-border rounded-xl">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3 bg-muted hover:bg-accent transition-colors ${isOpen ? "rounded-t-xl" : "rounded-xl"}`}
      >
        <div className="flex items-center gap-2">
          {isOpen ? <ChevronDown size={16} className="text-muted-foreground" /> : <ChevronRight size={16} className="text-muted-foreground" />}
          <span className="text-sm font-semibold text-foreground">{title}</span>
        </div>
        {count > 0 && (
          <span className="px-2 py-0.5 text-xs font-semibold text-white rounded-full bg-[#d7785b]">{count}</span>
        )}
      </button>
      {isOpen && <div className="p-4 space-y-4 relative">{children}</div>}
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b]" />
    </div>
  );
}

function NumberInput({ label, value, onChange, placeholder }: { label: string; value: string | number; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b]" />
    </div>
  );
}

function DateInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      <input type="date" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b] bg-card" />
    </div>
  );
}

function CheckboxInput({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-border text-[#d7785b] focus:ring-[#d7785b]" />
      <span className="text-sm text-muted-foreground">{label}</span>
    </label>
  );
}

function ToggleInput({ label, enabled, onChange }: { label: string; enabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between cursor-pointer py-1">
      <span className="text-sm text-muted-foreground">{label}</span>
      <button type="button" onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${enabled ? "bg-[#d7785b]" : "bg-input"}`}>
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-card transition-transform ${enabled ? "translate-x-[18px]" : "translate-x-[3px]"}`} />
      </button>
    </label>
  );
}

function SelectInput({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { label: string; value: string }[] }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b] bg-card">
        <option value="">All</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function MultiTextInput({ label, values, onChange, placeholder }: { label: string; values: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [inputVal, setInputVal] = useState("");
  const addValue = () => {
    const trimmed = inputVal.trim();
    if (trimmed && !values.includes(trimmed)) { onChange([...values, trimmed]); setInputVal(""); }
  };
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      <div className="flex gap-2">
        <input type="text" value={inputVal} onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addValue())} placeholder={placeholder}
          className="flex-1 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b]" />
        <button onClick={addValue} className="px-3 py-2 text-sm bg-muted border border-border rounded-lg hover:bg-accent">Add</button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {values.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-orange-50 dark:bg-orange-500/10 text-[#d7785b] border border-orange-200 dark:border-orange-500/30 rounded-md">
              {v}
              <button onClick={() => onChange(values.filter((x) => x !== v))} className="hover:text-red-600 dark:hover:text-red-400"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function countActive(filters: Record<string, any>, keys: string[]): number {
  return keys.filter((k) => {
    const v = filters[k];
    if (v === undefined || v === null || v === "" || v === false) return false;
    if (Array.isArray(v) && v.length === 0) return false;
    if (typeof v === "object" && !Array.isArray(v) && v.enabled === false) return false;
    return true;
  }).length;
}

// --- Main component ---

export function StoreCensusFilterBuilder({ filters, onFiltersChange, onSaveFilter }: FilterBuilderProps) {
  const [openSections, setOpenSections] = useState<string[]>(["location"]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveDescription, setSaveDescription] = useState("");

  const toggleSection = (key: string) => {
    setOpenSections((prev) => prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]);
  };

  const set = (key: string, value: any) => onFiltersChange({ ...filters, [key]: value });
  const get = (key: string, fallback: any = "") => filters[key] ?? fallback;

  // ── Dynamic app data ──
  const [appCategories, setAppCategories] = useState<StoreCensusAppCategory[]>([]);
  const [appSearch, setAppSearch] = useState("");
  const [appResults, setAppResults] = useState<StoreCensusApp[]>([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>();

  useEffect(() => {
    adminLeadsService.getAppCategories().then((res) => setAppCategories(res.data)).catch(() => {});
  }, []);

  const searchApps = useCallback(async (search: string, categoryId?: number) => {
    setAppsLoading(true);
    try {
      const res = await adminLeadsService.getApps({ search: search || undefined, categoryId, pageSize: 50 });
      setAppResults(res.data);
    } catch {
      setAppResults([]);
    } finally {
      setAppsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (!openSections.includes("apps")) return;
    const timer = setTimeout(() => searchApps(appSearch, selectedCategoryId), 400);
    return () => clearTimeout(timer);
  }, [appSearch, selectedCategoryId, openSections, searchApps]);

  // Fetch on first open
  useEffect(() => {
    if (openSections.includes("apps") && appResults.length === 0 && !appsLoading) {
      searchApps("", selectedCategoryId);
    }
  }, [openSections]); // eslint-disable-line react-hooks/exhaustive-deps

  const appCategoryOptions = useMemo(
    () => appCategories.map((c) => ({ value: String(c.category_id), label: `${c.name} (${c.app_count})` })),
    [appCategories]
  );

  const selectedAppIds: string[] = get("apps", []);

  const toggleApp = (appId: string) => {
    const cur: string[] = get("apps", []);
    set("apps", cur.includes(appId) ? cur.filter((x) => x !== appId) : [...cur, appId]);
  };

  const handleSave = () => {
    if (saveName.trim() && onSaveFilter) {
      onSaveFilter(saveName.trim(), saveDescription.trim());
      setShowSaveDialog(false);
      setSaveName("");
      setSaveDescription("");
    }
  };

  const countryOptions = useMemo(() => countries.map((c) => ({ value: c.code, label: c.name })), []);

  const selectedCountries: string[] = get("country", []);
  const availableStates = useMemo(() => {
    const states: { value: string; label: string }[] = [];
    for (const cc of selectedCountries) {
      const s = statesByCountry[cc];
      if (s) {
        for (const st of s) {
          states.push({ value: st.code, label: `${st.name} (${cc})` });
        }
      }
    }
    return states;
  }, [selectedCountries]);

  const currencySelectOptions = useMemo(() => currencyOptions.map((c) => ({ value: c.value, label: c.label })), []);

  return (
    <div className="space-y-3">

      {/* ── Location ── */}
      <FilterSection title="Location" count={countActive(filters, ["country", "state"])}
        isOpen={openSections.includes("location")} onToggle={() => toggleSection("location")}>
        <MultiSelectDropdown label="Countries (ISO alpha-2)" options={countryOptions} selected={get("country", [])}
          onChange={(v) => set("country", v)} searchable placeholder="Select countries..." />
        {availableStates.length > 0 && (
          <MultiSelectDropdown label="States / Provinces" options={availableStates} selected={get("state", [])}
            onChange={(v) => set("state", v)} searchable placeholder="Select states..." />
        )}
        {selectedCountries.length > 0 && availableStates.length === 0 && (
          <MultiTextInput label="State (type abbreviation)" values={get("state", [])} onChange={(v) => set("state", v)} placeholder="e.g. CA, NY" />
        )}
      </FilterSection>

      {/* ── Business ── */}
      <FilterSection title="Business" count={countActive(filters, ["vertical", "language", "employees", "websiteCreatedAt", "websiteUpdatedAt", "domainExtension"])}
        isOpen={openSections.includes("business")} onToggle={() => toggleSection("business")}>
        <MultiSelectDropdown label="Vertical / Industry" options={VERTICAL_OPTIONS} selected={get("vertical", [])}
          onChange={(v) => set("vertical", v)} searchable placeholder="Select verticals..." />
        <MultiTextInput label="Language" values={get("language", [])} onChange={(v) => set("language", v)} placeholder="e.g. English, Spanish" />
        <MultiSelectDropdown label="Employee Size Range" options={employeeSizeOptions} selected={get("employees", [])}
          onChange={(v) => set("employees", v)} placeholder="Select ranges..." />
        <MultiTextInput label="Domain Extension" values={get("domainExtension", [])} onChange={(v) => set("domainExtension", v)} placeholder="e.g. .com, .co.uk" />
        <div className="space-y-2">
          <label className="block text-xs font-medium text-muted-foreground">Website Created</label>
          <div className="grid grid-cols-2 gap-3">
            <DateInput label="From" value={get("websiteCreatedAt", {}).startDate || ""}
              onChange={(v) => set("websiteCreatedAt", { enabled: true, startDate: v, endDate: get("websiteCreatedAt", {}).endDate })} />
            <DateInput label="To" value={get("websiteCreatedAt", {}).endDate || ""}
              onChange={(v) => set("websiteCreatedAt", { enabled: true, startDate: get("websiteCreatedAt", {}).startDate, endDate: v })} />
          </div>
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-medium text-muted-foreground">Website Updated (Last Crawled)</label>
          <div className="grid grid-cols-2 gap-3">
            <DateInput label="From" value={get("websiteUpdatedAt", {}).startDate || ""}
              onChange={(v) => set("websiteUpdatedAt", { enabled: true, startDate: v, endDate: get("websiteUpdatedAt", {}).endDate })} />
            <DateInput label="To" value={get("websiteUpdatedAt", {}).endDate || ""}
              onChange={(v) => set("websiteUpdatedAt", { enabled: true, startDate: get("websiteUpdatedAt", {}).startDate, endDate: v })} />
          </div>
        </div>
      </FilterSection>

      {/* ── Contact ── */}
      <FilterSection title="Contact" count={countActive(filters, ["contact", "email_type"])}
        isOpen={openSections.includes("contact")} onToggle={() => toggleSection("contact")}>
        <MultiSelectDropdown label="Contact Availability" options={contactTypeOptions} selected={get("contact", [])}
          onChange={(v) => set("contact", v)} placeholder="e.g. email, phone" />
        <MultiSelectDropdown label="Email Type" options={emailTypeOptions} selected={get("email_type", [])}
          onChange={(v) => set("email_type", v)} placeholder="e.g. founder_personal, generic" />
      </FilterSection>

      {/* ── E-commerce ── */}
      <FilterSection title="E-commerce" count={countActive(filters, ["theme", "currency", "productPagesMin", "productPagesMax", "productVariantsMin", "productVariantsMax", "avgProductPriceMin", "avgProductPriceMax"])}
        isOpen={openSections.includes("ecommerce")} onToggle={() => toggleSection("ecommerce")}>
        <MultiTextInput label="Theme" values={get("theme", [])} onChange={(v) => set("theme", v)} placeholder="e.g. Dawn, Debut" />
        <MultiSelectDropdown label="Currency" options={currencySelectOptions} selected={get("currency", [])}
          onChange={(v) => set("currency", v)} searchable placeholder="Select currencies..." />
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Product Pages Min" value={get("productPagesMin")} onChange={(v) => set("productPagesMin", v)} placeholder="0" />
          <NumberInput label="Product Pages Max" value={get("productPagesMax")} onChange={(v) => set("productPagesMax", v)} placeholder="10000" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Variants Min" value={get("productVariantsMin")} onChange={(v) => set("productVariantsMin", v)} placeholder="0" />
          <NumberInput label="Variants Max" value={get("productVariantsMax")} onChange={(v) => set("productVariantsMax", v)} placeholder="100000" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Avg Price Min ($)" value={get("avgProductPriceMin")} onChange={(v) => set("avgProductPriceMin", v)} placeholder="0" />
          <NumberInput label="Avg Price Max ($)" value={get("avgProductPriceMax")} onChange={(v) => set("avgProductPriceMax", v)} placeholder="1000" />
        </div>
      </FilterSection>

      {/* ── Financial ── */}
      <FilterSection title="Financial" count={countActive(filters, ["estimatedSales", "technologySpend", "estimatedVisits"])}
        isOpen={openSections.includes("financial")} onToggle={() => toggleSection("financial")}>
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Est. Sales Min" value={(get("estimatedSales", []) as any[])[0] ?? ""} onChange={(v) => { const cur = get("estimatedSales", []); set("estimatedSales", [v, cur[1] ?? ""]); }} placeholder="5000" />
          <NumberInput label="Est. Sales Max" value={(get("estimatedSales", []) as any[])[1] ?? ""} onChange={(v) => { const cur = get("estimatedSales", []); set("estimatedSales", [cur[0] ?? "", v]); }} placeholder="100000" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Tech Spend Min" value={(get("technologySpend", []) as any[])[0] ?? ""} onChange={(v) => { const cur = get("technologySpend", []); set("technologySpend", [v, cur[1] ?? ""]); }} placeholder="100" />
          <NumberInput label="Tech Spend Max" value={(get("technologySpend", []) as any[])[1] ?? ""} onChange={(v) => { const cur = get("technologySpend", []); set("technologySpend", [cur[0] ?? "", v]); }} placeholder="5000" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Est. Visits Min" value={(get("estimatedVisits", []) as any[])[0] ?? ""} onChange={(v) => { const cur = get("estimatedVisits", []); set("estimatedVisits", [v, cur[1] ?? ""]); }} placeholder="5000" />
          <NumberInput label="Est. Visits Max" value={(get("estimatedVisits", []) as any[])[1] ?? ""} onChange={(v) => { const cur = get("estimatedVisits", []); set("estimatedVisits", [cur[0] ?? "", v]); }} placeholder="100000" />
        </div>
      </FilterSection>

      {/* ── Traffic ── */}
      <FilterSection title="Traffic" count={countActive(filters, ["totalTrafficMin", "totalTrafficMax", "organicTrafficMin", "organicTrafficMax", "paidTrafficMin", "paidTrafficMax"])}
        isOpen={openSections.includes("traffic")} onToggle={() => toggleSection("traffic")}>
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Total Traffic Min" value={get("totalTrafficMin")} onChange={(v) => set("totalTrafficMin", v)} placeholder="10000" />
          <NumberInput label="Total Traffic Max" value={get("totalTrafficMax")} onChange={(v) => set("totalTrafficMax", v)} placeholder="100000" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Organic Traffic Min" value={get("organicTrafficMin")} onChange={(v) => set("organicTrafficMin", v)} placeholder="5000" />
          <NumberInput label="Organic Traffic Max" value={get("organicTrafficMax")} onChange={(v) => set("organicTrafficMax", v)} placeholder="50000" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Paid Traffic Min" value={get("paidTrafficMin")} onChange={(v) => set("paidTrafficMin", v)} placeholder="1000" />
          <NumberInput label="Paid Traffic Max" value={get("paidTrafficMax")} onChange={(v) => set("paidTrafficMax", v)} placeholder="10000" />
        </div>
      </FilterSection>

      {/* ── Apps ── */}
      <FilterSection title="Apps" count={countActive(filters, ["apps", "installedDateFrom", "installedDateTo", "uninstalledDateFrom", "uninstalledDateTo", "verifiedAppInstalls", "uninstalledAppsOnly"])}
        isOpen={openSections.includes("apps")} onToggle={() => toggleSection("apps")}>

        {/* Category filter */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Filter by Category</label>
          <select value={selectedCategoryId ?? ""} onChange={(e) => setSelectedCategoryId(e.target.value ? parseInt(e.target.value) : undefined)}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b] bg-card">
            <option value="">All Categories</option>
            {appCategoryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* App search */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Search Apps</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input type="text" value={appSearch} onChange={(e) => setAppSearch(e.target.value)} placeholder="e.g. Klaviyo, ReCharge..."
              className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b]" />
          </div>
        </div>

        {/* App results */}
        <div className="border border-border rounded-lg overflow-hidden">
          {appsLoading ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader size={16} className="animate-spin mr-2" /> Loading apps...
            </div>
          ) : appResults.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">No apps found</div>
          ) : (
            <div className="max-h-48 overflow-y-auto divide-y divide-border">
              {appResults.map((app) => {
                const id = String(app.app_id);
                const selected = selectedAppIds.includes(id);
                return (
                  <div key={id} onClick={() => toggleApp(id)}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent ${selected ? "bg-orange-50 dark:bg-orange-500/10" : ""}`}>
                    <div className={`w-4 h-4 flex-shrink-0 flex items-center justify-center border rounded ${selected ? "border-[#d7785b] bg-[#d7785b]" : "border-border"}`}>
                      {selected && <Check size={10} className="text-white" />}
                    </div>
                    {app.icon_url && <img src={app.icon_url} alt="" className="w-6 h-6 rounded flex-shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{app.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{app.main_category?.name} · ★ {app.rating?.toFixed(1)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected apps summary */}
        {selectedAppIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selectedAppIds.map((id) => {
              const app = appResults.find((a) => String(a.app_id) === id);
              return (
                <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-orange-50 dark:bg-orange-500/10 text-[#d7785b] border border-orange-200 dark:border-orange-500/30 rounded-md">
                  {app?.name || `App #${id}`}
                  <button onClick={() => toggleApp(id)} className="hover:text-red-600 dark:hover:text-red-400"><X size={10} /></button>
                </span>
              );
            })}
          </div>
        )}

        <CheckboxInput label="Verified Installs Only" checked={get("verifiedAppInstalls", []).includes("true")}
          onChange={(v) => onFiltersChange(v
            ? { ...filters, verifiedAppInstalls: ["true"], uninstalledAppsOnly: [] }
            : { ...filters, verifiedAppInstalls: [] })} />
        <CheckboxInput label="Uninstalled Apps Only" checked={get("uninstalledAppsOnly", []).includes("true")}
          onChange={(v) => onFiltersChange(v
            ? { ...filters, uninstalledAppsOnly: ["true"], verifiedAppInstalls: [] }
            : { ...filters, uninstalledAppsOnly: [] })} />
        <div className="grid grid-cols-2 gap-3">
          <DateInput label="Installed After" value={get("installedDateFrom")} onChange={(v) => set("installedDateFrom", v)} />
          <DateInput label="Installed Before" value={get("installedDateTo")} onChange={(v) => set("installedDateTo", v)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <DateInput label="Uninstalled After" value={get("uninstalledDateFrom")} onChange={(v) => set("uninstalledDateFrom", v)} />
          <DateInput label="Uninstalled Before" value={get("uninstalledDateTo")} onChange={(v) => set("uninstalledDateTo", v)} />
        </div>
      </FilterSection>

      {/* ── Technology ── */}
      <FilterSection title="Technology" count={countActive(filters, ["trackingAnalytics"])}
        isOpen={openSections.includes("technology")} onToggle={() => toggleSection("technology")}>
        <MultiTextInput label="Tracking / Analytics" values={get("trackingAnalytics", [])} onChange={(v) => set("trackingAnalytics", v)}
          placeholder="e.g. Google Analytics, Facebook Pixel" />
      </FilterSection>

      {/* ── Activity Signals ── */}
      <FilterSection title="Activity Signals" count={countActive(filters, [
        "activityDelta", "activity_events_any", "activity_events_app_installed", "activity_events_app_uninstalled",
        "activity_events_tech_installed", "activity_events_tech_uninstalled", "activity_events_products_changed",
        "activity_events_emails_changed", "activity_events_theme_changed",
        "activity_freshness_last_activity_at",
      ])} isOpen={openSections.includes("activity")} onToggle={() => toggleSection("activity")}>
        <SelectInput label="Activity Delta Period" value={get("activityDelta")} onChange={(v) => set("activityDelta", v)}
          options={[{ label: "Last 7 days", value: "7" }, { label: "Last 30 days", value: "30" }, { label: "Last 90 days", value: "90" }]} />
        <div className="space-y-2">
          <span className="text-xs font-medium text-muted-foreground">Activity Events</span>
          <CheckboxInput label="Any Event" checked={get("activity_events_any", false)} onChange={(v) => set("activity_events_any", v)} />
          <CheckboxInput label="App Installed" checked={get("activity_events_app_installed", false)} onChange={(v) => set("activity_events_app_installed", v)} />
          <CheckboxInput label="App Uninstalled" checked={get("activity_events_app_uninstalled", false)} onChange={(v) => set("activity_events_app_uninstalled", v)} />
          <CheckboxInput label="Tech Installed" checked={get("activity_events_tech_installed", false)} onChange={(v) => set("activity_events_tech_installed", v)} />
          <CheckboxInput label="Tech Uninstalled" checked={get("activity_events_tech_uninstalled", false)} onChange={(v) => set("activity_events_tech_uninstalled", v)} />
          <CheckboxInput label="Products Changed" checked={get("activity_events_products_changed", false)} onChange={(v) => set("activity_events_products_changed", v)} />
          <CheckboxInput label="Emails Changed" checked={get("activity_events_emails_changed", false)} onChange={(v) => set("activity_events_emails_changed", v)} />
          <CheckboxInput label="Theme Changed" checked={get("activity_events_theme_changed", false)} onChange={(v) => set("activity_events_theme_changed", v)} />
        </div>
        <SelectInput label="Last Activity Freshness" value={get("activity_freshness_last_activity_at", {}).preset || ""}
          onChange={(v) => set("activity_freshness_last_activity_at", v ? { preset: v } : undefined)} options={freshnessPresetOptions} />
        <SelectInput label="Last App Event Freshness" value={get("activity_freshness_last_app_event_at", {}).preset || ""}
          onChange={(v) => set("activity_freshness_last_app_event_at", v ? { preset: v } : undefined)} options={freshnessPresetOptions} />
        <SelectInput label="Last Email Found Freshness" value={get("activity_freshness_last_email_found_at", {}).preset || ""}
          onChange={(v) => set("activity_freshness_last_email_found_at", v ? { preset: v } : undefined)} options={freshnessPresetOptions} />
        <SelectInput label="Last Product Update Freshness" value={get("activity_freshness_last_product_update_at", {}).preset || ""}
          onChange={(v) => set("activity_freshness_last_product_update_at", v ? { preset: v } : undefined)} options={freshnessPresetOptions} />
        <SelectInput label="Last Technology Change Freshness" value={get("activity_freshness_last_technology_change_at", {}).preset || ""}
          onChange={(v) => set("activity_freshness_last_technology_change_at", v ? { preset: v } : undefined)} options={freshnessPresetOptions} />
      </FilterSection>

      {/* ── Export ── */}
      <FilterSection title="Export" count={countActive(filters, ["show_exported", "exclude_exported"])}
        isOpen={openSections.includes("export")} onToggle={() => toggleSection("export")}>
        <ToggleInput label="Show Only Exported Stores" enabled={get("show_exported", false)} onChange={(v) => set("show_exported", v)} />
        <ToggleInput label="Exclude Exported Stores" enabled={get("exclude_exported", false)} onChange={(v) => set("exclude_exported", v)} />
      </FilterSection>

      {/* ── Save Filter ── */}
      {onSaveFilter && (
        <div className="pt-2">
          {!showSaveDialog ? (
            <button onClick={() => setShowSaveDialog(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-muted-foreground bg-card border border-border rounded-lg hover:bg-accent transition-colors w-full justify-center">
              <Save size={16} /> Save This Filter
            </button>
          ) : (
            <div className="p-4 bg-muted border border-border rounded-xl space-y-3">
              <TextInput label="Filter Name *" value={saveName} onChange={setSaveName} placeholder="My Custom Filter" />
              <TextInput label="Description" value={saveDescription} onChange={setSaveDescription} placeholder="Optional description" />
              <div className="flex gap-2">
                <button onClick={() => setShowSaveDialog(false)} className="flex-1 px-3 py-2 text-sm border border-border rounded-lg hover:bg-accent">Cancel</button>
                <button onClick={handleSave} disabled={!saveName.trim()} className="flex-1 px-3 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50" style={{ backgroundColor: "#d7785b" }}>Save</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
